import { useSignalR, type ConnectionState } from './useSignalR.tsx';

/**
 * Maps SignalR connection state to the live-freshness vocabulary.
 *
 * Maps the SignalR `useSignalR` hook's `connectionState` (`'Connecting' |
 * 'Connected' | 'Reconnecting' | 'Disconnected'`) into the simpler
 * vocabulary `LiveFreshnessIndicator` expects:
 *
 *   - `'live'`         — the hub is connected and receiving updates
 *   - `'reconnecting'` — the hub is mid-reconnect (transient disconnect)
 *   - `'offline'`      — the hub failed to connect or stopped trying
 *   - `'replay'`       — passed in by callers in History/replay mode (not derivable from SignalR alone)
 *
 * The third state — `'replay'` — is operator-driven (Time Spine moves to
 * a past window, the page goes into History/Explore mode), and so is
 * passed through from the caller's `mode` context rather than computed
 * here. This hook only translates the SignalR state.
 *
 * Wire pattern at the call site:
 *
 * ```tsx
 * const hubState = useHubConnectionState('alarms');  // 'live' | 'reconnecting' | 'offline'
 * <LiveFreshnessIndicator
 *   lastUpdate={data?.timestamp}
 *   connectionState={mode === 'history' ? 'replay' : hubState}
 *   staleAfterSeconds={30}
 * />
 * ```
 *
 * @param hubName same vocabulary `useSignalR` accepts (PascalCase
 *                "AlarmHub" or lowercase "alarms" both work).
 */
export type LiveConnectionState = 'live' | 'reconnecting' | 'offline';

export const useHubConnectionState = (hubName: string): LiveConnectionState => {
  const { connectionState } = useSignalR(hubName);
  return mapConnectionState(connectionState);
};

/**
 * Pure mapper exported separately for unit testing — given the raw
 * SignalR state, returns the LiveFreshnessIndicator-compatible state.
 */
export const mapConnectionState = (state: ConnectionState): LiveConnectionState => {
  switch (state) {
    case 'Connected':
      return 'live';
    case 'Connecting':
    case 'Reconnecting':
      return 'reconnecting';
    case 'Disconnected':
    case 'FailedPermanently':
    default:
      return 'offline';
  }
};
